import { useId } from "react";
import { Recommendation } from "../../types";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { H3 } from "../Headers/Headings";
import { getQuoteName } from "../../lib/getQuoteName";
import { fixNewlines } from "@/lib/text";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import styles from "./RecommendationPicker.module.css";

type RecommendationPickerProps = {
  available: Recommendation[];
  selectedIds: string[];
  max: number;
  onChange: (ids: string[]) => void;
};

// Lets the card owner pick which of the recommendations given inside the card's survey interval
// should be published with the card. Capped at `max` so the card stays readable.
const RecommendationPicker = ({ available, selectedIds, max, onChange }: RecommendationPickerProps) => {
  const t = useTranslations("cardForm");
  const d = useDateFormatter();
  const headingId = useId();
  const limitReached = selectedIds.length >= max;

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
      return;
    }
    if (limitReached) {
      return;
    }
    onChange([...selectedIds, id]);
  };

  return (
    <fieldset className={styles.picker} aria-describedby={`${headingId}-info`}>
      <legend className={styles.legend}>
        <H3 id={headingId} variant='noTop'>
          {t("recommendationsHeading")}
        </H3>
      </legend>
      <p id={`${headingId}-info`}>{t("recommendationsInfo", max)}</p>
      {available.length === 0 ? (
        <InfoMessage>{t("noRecommendations")}</InfoMessage>
      ) : (
        <>
          <ul className={styles.list}>
            {available.map((r) => {
              const isSelected = selectedIds.includes(r.id);
              return (
                <li className={styles.item} key={r.id}>
                  <label className={[styles.label, isSelected && styles.labelSelected].filter(Boolean).join(" ")}>
                    <input
                      type='checkbox'
                      className={styles.checkbox}
                      name='recommendationIds'
                      value={r.id}
                      checked={isSelected}
                      disabled={!isSelected && limitReached}
                      onChange={() => toggle(r.id)}
                    />
                    <span className={styles.body}>
                      <span className={styles.quote}>{fixNewlines(r.recommendation || "")}</span>
                      <span className={styles.meta}>
                        {getQuoteName(r.recommendationName, r.recommendationTitle, r.email)}
                        {r.respondedAt ? ` (${d(r.respondedAt)})` : ""}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <p className={styles.status} role='status'>
            {limitReached ? t("recommendationsLimitReached", max) : t("recommendationsSelected", selectedIds.length, max)}
          </p>
        </>
      )}
    </fieldset>
  );
};

export default RecommendationPicker;
